<template>
  <div class="salesflow-score-card">
    <div class="score-header">
      <h5 class="score-title">
        <feather-icon icon="ActivityIcon" size="18" class="mr-50" />
        SalesFlow Score
      </h5>
      <b-badge :variant="scoreVariant" pill class="score-badge-label">
        {{ scoreLabel }}
      </b-badge>
    </div>

    <div class="score-body">
      <div class="gauge-section">
        <div class="gauge-container">
          <svg viewBox="0 0 200 200" class="gauge-svg">
            <circle cx="100" cy="100" r="85" fill="none" :stroke="trackColor" stroke-width="12" stroke-linecap="round"
              :stroke-dasharray="circumference" :stroke-dashoffset="0"
              transform="rotate(-225 100 100)" />
            <circle cx="100" cy="100" r="85" fill="none" :stroke="scoreColor" stroke-width="12" stroke-linecap="round"
              :stroke-dasharray="circumference" :stroke-dashoffset="gaugeOffset"
              transform="rotate(-225 100 100)" class="gauge-progress" />
          </svg>
          <div class="gauge-center">
            <span class="gauge-value">{{ animatedScore }}</span>
            <span class="gauge-max">/100</span>
          </div>
        </div>
      </div>

      <div class="dimensions-section">
        <div v-for="(dim, key) in scoreData.dimensions" :key="key" class="dimension-row">
          <div class="dimension-info">
            <feather-icon :icon="dim.icon" size="14" class="dimension-icon" />
            <span class="dimension-label">{{ dim.label }}</span>
            <span class="dimension-weight">({{ dim.weight }}%)</span>
          </div>
          <div class="dimension-bar-wrap">
            <div class="dimension-bar-track">
              <div class="dimension-bar-fill" :style="{ width: dim.score + '%', backgroundColor: getDimensionColor(dim.score) }" />
            </div>
            <span class="dimension-score">{{ dim.score }}</span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="scoreData.alerts && scoreData.alerts.length" class="score-alerts">
      <div v-for="(alert, i) in scoreData.alerts" :key="i" :class="['alert-item', 'alert-' + alert.type]">
        <feather-icon :icon="alert.icon" size="14" class="alert-icon" />
        <div class="alert-content">
          <span class="alert-message">{{ alert.message }}</span>
          <div v-if="alert.details && alert.details.length" class="alert-details">
            <span v-for="(d, j) in alert.details" :key="j" class="alert-detail-item">
              {{ d.nome }} ({{ d.diasSemCompra }}d)
            </span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="scoreData.rawMetrics" class="score-metrics">
      <div class="metric-item">
        <span class="metric-label">Faturamento 3m</span>
        <span class="metric-value">{{ formatCurrency(scoreData.rawMetrics.valorAtual) }}</span>
      </div>
      <div class="metric-item">
        <span class="metric-label">Clientes Ativos</span>
        <span class="metric-value">{{ scoreData.rawMetrics.clientesAtivos }}/{{ scoreData.rawMetrics.carteiraTotal }}</span>
      </div>
      <div class="metric-item">
        <span class="metric-label">Pedidos 3m</span>
        <span class="metric-value">{{ scoreData.rawMetrics.totalPedidosAtual }}</span>
      </div>
      <div class="metric-item">
        <span class="metric-label">Itens Distintos</span>
        <span class="metric-value">{{ scoreData.rawMetrics.itensDistintos }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { BBadge } from 'bootstrap-vue';

export default {
  name: 'SalesFlowScore',
  components: { BBadge },
  props: {
    scoreData: {
      type: Object,
      default: () => ({ totalScore: 0, dimensions: {}, alerts: [], rawMetrics: {} }),
    },
  },
  data() {
    return {
      animatedScore: 0,
    };
  },
  computed: {
    circumference() {
      return 2 * Math.PI * 85 * 0.75;
    },
    gaugeOffset() {
      const pct = this.scoreData.totalScore / 100;
      return this.circumference * (1 - pct);
    },
    scoreColor() {
      const s = this.scoreData.totalScore;
      if (s >= 70) return '#16a34a';
      if (s >= 40) return '#f59e0b';
      return '#dc2626';
    },
    trackColor() {
      return 'var(--gauge-track, #e9ecef)';
    },
    scoreVariant() {
      const s = this.scoreData.totalScore;
      if (s >= 70) return 'light-success';
      if (s >= 40) return 'light-warning';
      return 'light-danger';
    },
    scoreLabel() {
      const s = this.scoreData.totalScore;
      if (s >= 80) return 'Excelente';
      if (s >= 70) return 'Bom';
      if (s >= 50) return 'Regular';
      if (s >= 30) return 'Atenção';
      return 'Crítico';
    },
  },
  watch: {
    'scoreData.totalScore'(newVal) {
      this.animateScore(newVal);
    },
  },
  mounted() {
    this.animateScore(this.scoreData.totalScore);
  },
  methods: {
    animateScore(target) {
      const start = this.animatedScore;
      const diff = target - start;
      const duration = 1200;
      const startTime = performance.now();
      const step = (timestamp) => {
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        this.animatedScore = Math.round(start + diff * eased);
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    },
    getDimensionColor(score) {
      if (score >= 70) return '#16a34a';
      if (score >= 40) return '#f59e0b';
      return '#dc2626';
    },
    formatCurrency(value) {
      return 'R$ ' + Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
  },
};
</script>

<style scoped>
.salesflow-score-card {
  background: var(--card-bg, #fff);
  border-radius: 10px;
  box-shadow: 0 4px 24px rgba(34, 41, 47, 0.1);
  padding: 1.25rem;
  height: 100%;
}

.score-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.score-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-heading, #5e5873);
  margin: 0;
  display: flex;
  align-items: center;
}

.score-badge-label {
  font-size: 0.75rem;
  padding: 0.35em 0.75em;
}

.score-body {
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
}

.gauge-section {
  flex-shrink: 0;
}

.gauge-container {
  position: relative;
  width: 140px;
  height: 140px;
}

.gauge-svg {
  width: 100%;
  height: 100%;
}

.gauge-progress {
  transition: stroke-dashoffset 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.gauge-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -45%);
  text-align: center;
}

.gauge-value {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-heading, #5e5873);
  line-height: 1;
}

.gauge-max {
  display: block;
  font-size: 0.75rem;
  color: var(--text-muted, #b9b9c3);
}

.dimensions-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.dimension-row {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.dimension-info {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.dimension-icon {
  color: var(--text-muted, #b9b9c3);
}

.dimension-label {
  font-size: 0.78rem;
  color: var(--text-heading, #5e5873);
  font-weight: 500;
}

.dimension-weight {
  font-size: 0.65rem;
  color: var(--text-muted, #b9b9c3);
}

.dimension-bar-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.dimension-bar-track {
  flex: 1;
  height: 6px;
  background: var(--gauge-track, #e9ecef);
  border-radius: 3px;
  overflow: hidden;
}

.dimension-bar-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 1s ease;
}

.dimension-score {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-heading, #5e5873);
  min-width: 24px;
  text-align: right;
}

.score-alerts {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.alert-item {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-size: 0.8rem;
}

.alert-warning {
  background: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
}

.alert-danger {
  background: rgba(220, 38, 38, 0.1);
  color: #dc2626;
}

.alert-info {
  background: rgba(14, 165, 233, 0.1);
  color: #0ea5e9;
}

.alert-icon {
  flex-shrink: 0;
  margin-top: 1px;
}

.alert-content {
  flex: 1;
}

.alert-message {
  font-weight: 500;
}

.alert-details {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 0.75rem;
  margin-top: 0.25rem;
}

.alert-detail-item {
  font-size: 0.72rem;
  opacity: 0.85;
}

.score-metrics {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--gauge-track, #e9ecef);
}

.metric-item {
  text-align: center;
}

.metric-label {
  display: block;
  font-size: 0.65rem;
  color: var(--text-muted, #b9b9c3);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.metric-value {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-heading, #5e5873);
}

@media (max-width: 768px) {
  .score-body {
    flex-direction: column;
    align-items: center;
  }

  .score-metrics {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
